import type { ProposalKind } from '../../../api/agentSchemas';
import { matchFinance } from '../finance/intents';
import { normalize } from '../normalize';
import type { IntentId } from './taxonomy';

/**
 * The keywords the team already answered by before the router existed. They are precise on the
 * wording they were written for and blind to everything else, which is why the router reads them
 * second: it asks the model first and uses these when the model is unsure.
 */
const WORK_RULES: readonly { test: RegExp; intent: IntentId }[] = [
  { test: /^(ok|okey|okay|listo|vale|entendido|de acuerdo|ya veo)( gracias)?$/, intent: 'gracias' },
  { test: /seguimiento|no (han )?(vuelto|regresado)|no regresan|recuperar|inactiv|perdid|volver/, intent: 'winback' },
  { test: /confirm|manana|recordatorio|recordar|no ?show|falta/, intent: 'confirmar' },
  { test: /oferta|promo|descuento|flojo|llenar|vacio/, intent: 'oferta' },
  { test: /primero|empiezo|empezar|prioridad|que hago|hoy/, intent: 'primero' },
  { test: /como va|negocio|resumen/, intent: 'primero' },
  { test: /que (puedes|pueden|sabes|saben)|ayuda|hacer/, intent: 'puedo' },
  { test: /^(hola|buenas|buenos|hey)\b/, intent: 'saludo' },
];

const KIND_OF_KEYWORD: Readonly<Partial<Record<IntentId, ProposalKind>>> = {
  oferta: 'offer',
  winback: 'win_back',
  confirmar: 'confirm_visits',
};

/** The situation a keyword names, or null when no keyword matches. Money keywords come first. */
export function ruleIntent(text: string): IntentId | null {
  const money = matchFinance(text);
  if (money) return money.id;
  const said = normalize(text);
  return WORK_RULES.find((rule) => rule.test.test(said))?.intent ?? null;
}

/** The kind of work the keywords say a sentence is about ("esa oferta no me convence" is about the offer), or null. */
export function namedProposalKind(text: string): ProposalKind | null {
  const intent = ruleIntent(text);
  return intent ? (KIND_OF_KEYWORD[intent] ?? null) : null;
}
